import { bannerError } from 'client-portal/utils/error-handling';
import { reads } from 'macro-decorators';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import InfinityModel from 'ember-infinity/lib/infinity-model';
import Service, { service } from '@ember/service';

const PAGE_SIZE = 30;
const WEBSOCKET_CHANNEL_NAME = 'ClientPortal::AnnouncementsClientUpdatesChannel';

class ExtendedInfinityModel extends InfinityModel {
  buildParams() {
    let params = super.buildParams(...arguments);
    params.page = { before: this.before, size: PAGE_SIZE };
    return params;
  }

  afterInfinityModel(model) {
    this.canLoadMore = model.length >= PAGE_SIZE;
    this.before = model.lastObject?.id;
  }
}

const ACTIONS = {
  SENT: 'announcementSent',
  DELETED: 'announcementDeleted',
};

export default class AnnouncementService extends Service {
  @service actionCable;
  @service currentPractice;
  @service infinity;
  @service intl;
  @service router;
  @service session;
  @service store;
  @service fastboot;

  @tracked announcementsInfinityModel = [];

  @reads('queryAnnouncements.isRunning') isLoading;

  selectedProfileAnnouncementsChannel = null;

  get isAnnouncementsActive() {
    let isClientSelectionPage = this.router.currentRouteName === 'client-selection';
    return !isClientSelectionPage && this.currentPractice.get('announcementsAvailable');
  }

  async fetchAnnouncements() {
    if (this.fastboot.isFastBoot || !this.isAnnouncementsActive) {
      return;
    }

    await this.queryAnnouncements.perform();
  }

  async markAnnouncementsAsRead() {
    if (this.announcementsInfinityModel.any(announcement => announcement.readAt === null)) {
      let adapter = this.store.adapterFor('announcement');
      await adapter.readAnnouncements();

      this.infinity.replace(
        this.announcementsInfinityModel,
        this.announcementsInfinityModel.content.map(announcement => {
          if (announcement.readAt === null) {
            announcement.readAt = new Date().toISOString();
          }
          return announcement;
        })
      );
    }
  }

  queryAnnouncements = task(async () => {
    try {
      this.announcementsInfinityModel = await this.infinity.model(
        'announcement',
        {
          perPage: PAGE_SIZE,
          perPageParam: null,
          pageParam: null,
        },
        ExtendedInfinityModel
      );
    } catch (err) {
      bannerError(err, { title: this.intl.t('messages.error.announcementsLoadFailed') });
    }
  });

  get currentClientId() {
    return this.session.currentClient?.hashedId;
  }

  subscribeToSelectedProfileAnnouncementsChannel = async currentClientId => {
    if (this.fastboot.isFastBoot || !this.isAnnouncementsActive) return null;

    if (this.selectedProfileAnnouncementsChannel) {
      this.unsubscribeFromChannel();
    }

    if (!currentClientId) return null;

    this.selectedProfileAnnouncementsChannel = await this.actionCable.createSubscription(
      'announcementsClientUpdatesChannel',
      { channel: WEBSOCKET_CHANNEL_NAME, clientId: currentClientId },
      {
        received: response => {
          this.handleActivity(response);
        },
      }
    );
  };

  unsubscribeFromChannel() {
    this.actionCable.unsubscribe('announcementsClientUpdatesChannel');
    this.selectedProfileAnnouncementsChannel = null;
  }

  handleActivity(response) {
    if (!response.data || !response.data.id) return;

    if (response.action === ACTIONS.SENT) {
      this.addNewAnnouncement(response);
    } else if (response.action === ACTIONS.DELETED) {
      this.updateDeletedAnnouncement(response);
    }
  }

  addNewAnnouncement(response) {
    if (this.store.peekRecord('announcement', response.data.id)) return;

    this.store.pushPayload({ data: response.data });
    const announcement = this.store.peekRecord('announcement', response.data.id);

    if (announcement) {
      this.announcementsInfinityModel.unshiftObject(announcement);
    }
  }

  updateDeletedAnnouncement(response) {
    this.store.pushPayload({ data: response.data });
  }

  willDestroy() {
    this.unsubscribeFromChannel();
    super.willDestroy();
  }
}
